import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Segmented, Card, Typography, Space, Tag, Spin, Empty } from "antd";
import { CloseOutlined, ClockCircleOutlined, CalendarOutlined } from "@ant-design/icons";
import AxiosInstance from "../../services/axiosInstance"; // same pattern as your web axios
import { formatDate } from "../../utility/dateUtils";

const { Text } = Typography;

const STATUS = ["All", "Approved", "Denied", "Pending"];

const formatDateMMDDYYYY = (val) => {
  if (!val) return "-";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return String(val);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const statusTag = (status) => {
  const s = String(status || "");
  if (s === "Approved") return <Tag color="green">Approved</Tag>;
  if (s === "Denied") return <Tag color="red">Denied</Tag>;
  return <Tag color="gold">Pending</Tag>;
};

export default function ChangeRequestModal({
  open,
  onClose,
  title = "Request Status",
  height = 520,
}) {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("All");

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await AxiosInstance.get("emp/change-req-sch");
      const arr = data?.data?.toReversed() || [];
      setList(Array.isArray(arr) ? arr : []);
    } catch (e) {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ same as RN: clear list + reset filter on open, then fetch
  useEffect(() => {
    if (!open) return;
    setList([]);
    setSelectedStatus("All");
    fetchList();
  }, [open, fetchList]);

  const filteredList = useMemo(() => {
    if (selectedStatus === "All") return list;
    return (list || []).filter(
      (item) =>
        String(item?.changeRequest || "").toLowerCase() ===
        selectedStatus.toLowerCase()
    );
  }, [list, selectedStatus]);

  return (
    <Modal
      open={open}
      footer={null}
      width={780}
      closable={false}
      onCancel={onClose}
      bodyStyle={{ padding: 16 }}
      title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Text strong style={{ fontSize: 16 }}>
            {title}
          </Text>
          <CloseOutlined onClick={onClose} style={{ cursor: "pointer" }} />
        </div>
      }
    >
      {/* Filter */}
      <div
        style={{
          marginBottom: 12,
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <Segmented
          value={selectedStatus}
          onChange={setSelectedStatus}
          options={STATUS}
          width={100}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          Total: {filteredList?.length || 0}
        </Text>
      </div>

      {/* List */}
      <div style={{ maxHeight: height, overflowY: "auto", paddingRight: 4 }}>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "40px 0",
            }}
          >
            <Spin />
          </div>
        ) : filteredList?.length ? (
          <Space direction="vertical" size={10} style={{ width: "100%" }}>
            {filteredList.map((item, idx) => (
              <Card
                key={item?._id || idx}
                size="small"
                style={{
                  borderRadius: 12,
                  borderLeft: `4px solid ${
                    item?.changeRequest === "Approved"
                      ? "#52c41a"
                      : item?.changeRequest === "Denied"
                        ? "#ff4d4f"
                        : "#faad14"
                  }`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    padding: "10px",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <Text strong style={{ fontSize: 14 }}>
                      {item?.clientId?.fullname || "N/A"}{" "}
                            {item?.service?.desc && (
              <Text
        style={{
            fontSize: 9,
        }}
      >
        {item?.service?.desc}
      </Text>
    )}
                    </Text>

                    <div style={{ marginTop: 6 }}>
                      <Space size={12} wrap>
                        <Text type="secondary">
                          <CalendarOutlined />    {formatDate(item?.sDate, 'MM/DD/YYYY')}
                        </Text>
                        <Text type="secondary">
                          <ClockCircleOutlined /> {" "}
                          {item?.changeRequest === "Pending"
                            ? (item?.requestedData?.aSTime ??
                              item?.requestedData?.sTime)
                            : item?.sTime}
                         {" "} – {" "}
                          {item?.changeRequest === "Pending"
                            ? (item?.requestedData?.aETime ??
                              item?.requestedData?.eTime)
                            : item?.eTime}
                        </Text>
                      </Space>
                    </div>

                    <div style={{ marginTop: 6 }}>
                        {item?.changeRequest !== "Pending" && (<Text type="secondary" style={{ fontSize: 12 }}>
                        Reason: {item?.changeRequestReason || "-"}
                      </Text>  )}
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "flex-start" }}>
                    {statusTag(item?.changeRequest)}
                  </div>
                </div>
              </Card>
            ))}
          </Space>
        ) : (
          <Empty
            description={
              selectedStatus !== "All"
                ? `No ${selectedStatus} requests found`
                : "No requests found"
            }
          />
        )}
      </div>
    </Modal>
  );
}
